import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Search } from '@material-ui/icons';
import { useHistory } from 'react-router-dom';
import CircularLoader from '../loader/CircularLoader';
import { formatDate } from '../../scripts/utils';
import {
  getStorytellingOptions,
  IStorytellingSearchResponse,
  searchStorytelling,
} from '../../models/api/storytelling';
import { getStates } from '../../models/api/countries';
import ArchivedProjectLock from '../custom-components/ArchivedProjectLock';
import DatePickerField from '../custom-components/DatePickerField';
import {
  CONSTRUCTION_TYPE_OPTIONS,
  DELIVERY_METHOD_OPTIONS,
  getProjectFacilityOptions,
  PROJECT_SERVICE_OPTIONS,
  PROJECT_TYPE_OPTIONS,
} from '../project-lifecycle/data';
import useStorytellingAccess from './useStorytellingAccess';
import {
  buildStorytellingSearchRequest,
  digitsOnly,
  EMPTY_STORYTELLING_FILTERS,
  formatStorytellingDollars,
  formatStorytellingNumber,
  getStorytellingFilterError,
  hasStorytellingFilter,
  optionLabel,
  projectTypeLabel,
  pruneFacilityTypes,
  storytellingResultPath,
  StorytellingFilters,
} from './storytellingSearch';

const useStyles = makeStyles({
  titleStyle: {
    background: 'linear-gradient(225deg, #00308C 0%, #002366 100%)',
    borderRadius: '4px 4px 0px 0px',
  },
  fieldLabel: {
    color: '#757575',
    fontWeight: 600,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  row: {
    cursor: 'pointer',
  },
  // The multi-select options. They match the A3 Key Team Members list: Typography takes the
  // menu's ordinary text colour, where ListItemText would take the theme's pale blue, which is
  // tuned for the dark navigation drawer and all but vanishes on a white menu. Compact too: a
  // small checkbox and no extra vertical padding, so a long list of facility types stays short.
  option: {
    paddingTop: 0,
    paddingBottom: 0,
    minHeight: 36,
  },
  optionCheckbox: {
    padding: '6px 8px 6px 0',
  },
  optionText: {
    fontWeight: 'normal',
    fontSize: 15,
  },
});

// The A3 form offers "Other" alongside the project's own wording for it; there is no such wording
// to search by here, so the filter lists the named methods only.
const DELIVERY_METHODS = DELIVERY_METHOD_OPTIONS.filter((option) => option.value !== 'other');

const DEFAULT_ERROR = 'The search could not be completed. Please try again.';

const errorMessage = (error: any): string => error?.response?.data?.message || DEFAULT_ERROR;

/**
 * Dashboard's full-width "Storytelling" card, shown on the aggregated dashboard (no project
 * selected) to users with Storytelling access to at least one project. Its fields come from the
 * A3 Summary, the Project Request (Project Lifecycle) form and the project's address; once at
 * least one is set, Search lists the CENTERLINE projects that fit all of them -- archived ones
 * too, marked with the lock -- only among the projects the user has Storytelling access to, which
 * the API enforces.
 */
export default function DashboardStorytellingCard({ paperClassName }: { paperClassName?: string }) {
  const hasAccess = useStorytellingAccess();
  if (!hasAccess) return null;

  return (
    <Grid item xs={12}>
      <Paper className={paperClassName}>
        <StorytellingCardContent />
      </Paper>
    </Grid>
  );
}

function StorytellingCardContent() {
  const classes = useStyles();
  const history = useHistory();

  const [filters, setFilters] = useState<StorytellingFilters>(EMPTY_STORYTELLING_FILTERS);
  const [searching, setSearching] = useState(false);
  const [response, setResponse] = useState<IStorytellingSearchResponse | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [clients, setClients] = useState<string[]>([]);
  const [states, setStates] = useState<{ label: string; value: string }[]>([]);

  // The dropdowns' values. Without them a dropdown just offers "Any"; neither is worth an error.
  useEffect(() => {
    let current = true;
    getStorytellingOptions()
      .then((options) => current && setClients(options.clients))
      .catch(() => {});
    getStates()
      .then(
        (byName) =>
          current &&
          setStates(
            Object.keys(byName)
              .sort((a, b) => a.localeCompare(b))
              .map((name) => ({ label: name, value: byName[name] })),
          ),
      )
      .catch(() => {});
    return () => {
      current = false;
    };
  }, []);

  const filterError = getStorytellingFilterError(filters);
  const canSearch = hasStorytellingFilter(filters) && !filterError && !searching;

  const setFilter = (key: keyof StorytellingFilters, value: string | string[]) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      // As on the Project Request form, the facility types on offer follow the project type, so a
      // choice the new type no longer lists goes with it.
      if (key === 'projectType') {
        next.facilityTypes = pruneFacilityTypes(prev.facilityTypes, value as string);
      }
      return next;
    });
  };

  const handleSearch = async () => {
    if (!canSearch) return;
    setSearching(true);
    setSearchError(null);
    try {
      setResponse(await searchStorytelling(buildStorytellingSearchRequest(filters)));
    } catch (error) {
      setResponse(null);
      setSearchError(errorMessage(error));
    } finally {
      setSearching(false);
    }
  };

  const handleClear = () => {
    setFilters(EMPTY_STORYTELLING_FILTERS);
    setResponse(null);
    setSearchError(null);
  };

  const label = (text: string) => (
    <Typography variant="caption" className={classes.fieldLabel}>
      {text}
    </Typography>
  );

  const cell = (text: string, content: React.ReactNode) => (
    // minWidth 0: a grid item will not shrink below its content otherwise, so a long selection
    // would widen the whole column instead of being cut off with an ellipsis.
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      {label(text)}
      {content}
    </div>
  );

  // A pair of inputs side by side while each has room (its `flex` basis), one above the other
  // when the container is too narrow for both -- so a filled-in date or amount is never squeezed
  // out of sight on a phone.
  const range = (
    first: React.ReactNode,
    second: React.ReactNode,
    bases: [string, string] = ['1 1 150px', '1 1 150px'],
  ) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      <div style={{ flex: bases[0], minWidth: 0 }}>{first}</div>
      <div style={{ flex: bases[1], minWidth: 0 }}>{second}</div>
    </div>
  );

  const dateField = (key: keyof StorytellingFilters, ariaLabel: string, placeholder: string) => (
    <DatePickerField
      value={filters[key] as string}
      onChange={(date) => setFilter(key, date)}
      ariaLabel={ariaLabel}
      placeholder={placeholder}
    />
  );

  const numberField = (
    key: keyof StorytellingFilters,
    ariaLabel: string,
    placeholder: string,
    prefix?: string,
  ) => {
    const digits = filters[key] as string;
    return (
      <TextField
        variant="outlined"
        size="small"
        fullWidth
        placeholder={placeholder}
        value={digits ? Number(digits).toLocaleString('en-US') : ''}
        onChange={(e) => setFilter(key, digitsOnly(e.target.value))}
        inputProps={{ inputMode: 'numeric', 'aria-label': ariaLabel }}
        InputProps={
          prefix
            ? { startAdornment: <InputAdornment position="start">{prefix}</InputAdornment> }
            : undefined
        }
      />
    );
  };

  const selectField = (
    key: keyof StorytellingFilters,
    options: readonly { label: string; value: string }[],
    ariaLabel: string,
    anyLabel = 'Any',
  ) => (
    <TextField
      select
      variant="outlined"
      size="small"
      fullWidth
      value={filters[key] as string}
      onChange={(e) => setFilter(key, e.target.value)}
      SelectProps={{ displayEmpty: true }}
      inputProps={{ 'aria-label': ariaLabel }}
    >
      <MenuItem value="">
        <em>{anyLabel}</em>
      </MenuItem>
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );

  const multiSelectField = (
    key: 'facilityTypes' | 'serviceTypes',
    options: readonly { label: string; value: string }[],
    ariaLabel: string,
  ) => {
    const selected = filters[key];
    return (
      <TextField
        select
        variant="outlined"
        size="small"
        fullWidth
        value={selected}
        onChange={(e) => setFilter(key, (e.target.value as unknown) as string[])}
        SelectProps={{
          multiple: true,
          displayEmpty: true,
          // One choice names itself; more are counted, so a long selection cannot stretch the
          // field and push the form out of shape.
          renderValue: (value) => {
            const values = value as string[];
            if (!values.length) return <em>Any</em>;
            if (values.length === 1) return optionLabel(options, values[0]);
            return `${values.length} options selected`;
          },
        }}
        inputProps={{ 'aria-label': ariaLabel }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value} className={classes.option}>
            <Checkbox
              checked={selected.includes(option.value)}
              size="small"
              className={classes.optionCheckbox}
            />
            <Typography className={classes.optionText}>{option.label}</Typography>
          </MenuItem>
        ))}
      </TextField>
    );
  };

  return (
    <Card>
      <CardContent className={classes.titleStyle}>
        <h2 className="h2" style={{ textAlign: 'left', color: '#FFFFFF', margin: 0 }}>
          Storytelling
        </h2>
      </CardContent>
      <CardContent>
        <div
          style={{
            display: 'grid',
            // As many columns of at least 280px as fit, but never more than three -- so three on a
            // wide screen, two on a tablet, one on a phone, with no breakpoint to get wrong. A
            // third of the width (less the two 24px gaps) is the floor that caps it at three, and
            // min(100%, ...) keeps a very narrow screen to one column instead of overflowing it.
            gridTemplateColumns:
              'repeat(auto-fill, minmax(min(100%, max(280px, (100% - 48px) / 3)), 1fr))',
            gap: '16px 24px',
          }}
        >
          {cell(
            'Location',
            range(
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                placeholder="Address, city or location"
                value={filters.projectLocation}
                onChange={(e) => setFilter('projectLocation', e.target.value)}
                inputProps={{ 'aria-label': 'Location' }}
              />,
              selectField('stateOrProvinceId', states, 'State', 'Any state'),
              ['3 1 170px', '2 1 150px'],
            ),
          )}
          {cell(
            'Client',
            selectField(
              'clientName',
              clients.map((c) => ({ label: c, value: c })),
              'Client',
            ),
          )}
          {cell('Project Type', selectField('projectType', PROJECT_TYPE_OPTIONS, 'Project type'))}
          {cell(
            'Facility Type',
            multiSelectField(
              'facilityTypes',
              // The four general options, plus the selected project type's own.
              getProjectFacilityOptions(filters.projectType || undefined),
              'Facility type',
            ),
          )}
          {cell(
            'Service Type',
            multiSelectField('serviceTypes', PROJECT_SERVICE_OPTIONS, 'Service type'),
          )}
          {cell(
            'Delivery Method',
            selectField('deliveryMethod', DELIVERY_METHODS, 'Delivery method'),
          )}
          {cell(
            'Start Date (from – to)',
            range(
              dateField('startDateFrom', 'Start date from', 'From'),
              dateField('startDateTo', 'Start date to', 'To'),
            ),
          )}
          {cell(
            'Completion Date (from – to)',
            range(
              dateField('completionDateFrom', 'Completion date from', 'From'),
              dateField('completionDateTo', 'Completion date to', 'To'),
            ),
          )}
          {cell(
            'Construction Type',
            selectField('constructionType', CONSTRUCTION_TYPE_OPTIONS, 'Construction type'),
          )}
          {cell(
            'Construction Cost (min – max)',
            range(
              numberField('constructionCostMin', 'Min cost', 'Min', '$'),
              numberField('constructionCostMax', 'Max cost', 'Max', '$'),
            ),
          )}
          {cell(
            'Square Footage (min – max)',
            range(
              numberField('squareFootageMin', 'Min sq ft', 'Min'),
              numberField('squareFootageMax', 'Max sq ft', 'Max'),
            ),
          )}
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 12,
            marginTop: 24,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            startIcon={<Search />}
            disabled={!canSearch}
            onClick={handleSearch}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            color="primary"
            disabled={searching || (!hasStorytellingFilter(filters) && !response && !searchError)}
            onClick={handleClear}
          >
            Clear
          </Button>
          {filterError ? (
            <Typography variant="body2" color="error" role="alert">
              {filterError}
            </Typography>
          ) : null}
        </div>

        {searching ? <CircularLoader /> : null}

        {searchError ? (
          <Typography variant="body2" color="error" role="alert" style={{ marginTop: 16 }}>
            {searchError}
          </Typography>
        ) : null}

        {response && !searching ? (
          <StorytellingResults
            response={response}
            onOpen={(projectId) => history.push(storytellingResultPath(projectId))}
            rowClassName={classes.row}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}

function StorytellingResults({
  response,
  onOpen,
  rowClassName,
}: {
  response: IStorytellingSearchResponse;
  onOpen: (projectId: string) => void;
  rowClassName: string;
}) {
  const { results, total } = response;

  if (!results.length) {
    return (
      <Typography variant="body1" style={{ marginTop: 24 }}>
        No projects match these filters.
      </Typography>
    );
  }

  return (
    <div style={{ marginTop: 24 }}>
      <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
        {total === 1 ? '1 project' : `${total} projects`}
        {total > results.length ? ` (showing the first ${results.length})` : ''}
      </Typography>
      <TableContainer>
        <Table size="small" aria-label="Storytelling search results">
          <TableHead>
            <TableRow>
              <TableCell>Project</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Project Type</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Start</TableCell>
              <TableCell>Completion</TableCell>
              <TableCell align="right">Construction Cost</TableCell>
              <TableCell>Delivery Method</TableCell>
              <TableCell align="right">Square Footage</TableCell>
              <TableCell>Construction Type</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {results.map((result) => (
              <TableRow
                key={result.projectId}
                hover
                className={rowClassName}
                onClick={() => onOpen(result.projectId)}
              >
                <TableCell>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    {result.architectProjectNumber
                      ? `${result.architectProjectNumber} – ${result.projectName}`
                      : result.projectName}
                    {result.isArchived ? <ArchivedProjectLock /> : null}
                  </span>
                </TableCell>
                <TableCell>{result.clientName || '—'}</TableCell>
                <TableCell>
                  {projectTypeLabel(
                    PROJECT_TYPE_OPTIONS,
                    result.projectType,
                    result.projectTypeOther,
                  )}
                </TableCell>
                <TableCell>{result.projectLocation || '—'}</TableCell>
                <TableCell>{result.startDate ? formatDate(result.startDate, true) : '—'}</TableCell>
                <TableCell>
                  {result.completionDate ? formatDate(result.completionDate, true) : '—'}
                </TableCell>
                <TableCell align="right">
                  {formatStorytellingDollars(result.constructionCost)}
                </TableCell>
                <TableCell>{optionLabel(DELIVERY_METHOD_OPTIONS, result.deliveryMethod)}</TableCell>
                <TableCell align="right">
                  {formatStorytellingNumber(result.squareFootage)}
                </TableCell>
                <TableCell>
                  {optionLabel(CONSTRUCTION_TYPE_OPTIONS, result.constructionType)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
